import * as React from "react";

import { useTripleWhale } from "../../../Common";

import { ReactMyBagAnalyticsInterface } from "./analytics-interface";

/**
 * Triple Whale implementation of the Checkout2022 analytics interface (BBNT_TripleWhaleEvents,
 * docs/tw-events-plan.md §5 rows 6–7, 11–12 and §5.1). The funnel steps go through the
 * module's state machine: identity and `{i,q,v}` lines come from the backend transport, a
 * guest step waits for the captured e-mail, every step is deduplicated by its shop-scoped
 * eventId. `paymentSubmitted` and `Purchase` are server-rendered on the success page.
 *
 * The bag's custom events take their identity from the same transport: `view_cart`
 * carries the cart key (the transport) and the line count and value of the snapshot the
 * bag handed over — one state, even when the transport answers after the cart changed
 * elsewhere — once per distinct set of lines and page; `remove_from_cart` the `{i,v}` of
 * the removed product (the transport falls back to the catalogue when the line is
 * already gone) and the removed quantity.
 */
export const useAnalyticsTripleWhale = (): ReactMyBagAnalyticsInterface => {
  const {
    isConfigurationLoaded,
    currency,
    identify,
    fireCustom,
    submitFunnelStep,
    captureContact,
    consumerInitializationGuard,
  } = useTripleWhale();
  const sentCartViews = React.useRef(new Set<string>());

  const checkoutStarted: NonNullable<
    ReactMyBagAnalyticsInterface["checkoutStarted"]
  > = React.useCallback(
    ({ email }) => {
      void submitFunnelStep("checkoutStarted", { email: email || null });
    },
    [submitFunnelStep]
  );

  const shippingSubmitted: NonNullable<
    ReactMyBagAnalyticsInterface["shippingSubmitted"]
  > = React.useCallback(
    ({ methodCode, email }) => {
      void submitFunnelStep("shippingSubmitted", {
        methodCode,
        email: email || null,
      });
    },
    [submitFunnelStep]
  );

  const contactCaptured: NonNullable<
    ReactMyBagAnalyticsInterface["contactCaptured"]
  > = React.useCallback(
    ({ email }) => {
      void captureContact(email);
    },
    [captureContact]
  );

  const removeFromCart: NonNullable<
    ReactMyBagAnalyticsInterface["removeFromCart"]
  > = React.useCallback(
    ({ productId, quantity }) => {
      if (!productId || !(quantity > 0)) {
        return;
      }
      void identify([{ product_id: productId, qty: quantity }]).then(
        (identity) => {
          const line = identity?.items?.[0];
          if (!identity || !line) {
            return;
          }
          return fireCustom("remove_from_cart", {
            category: "engagement",
            subcategory: "button_click",
            item_id: line.i,
            variant_id: line.v,
            quantity,
            cart_key: identity.cartKey,
          });
        }
      );
    },
    [identify, fireCustom]
  );

  const viewCart: NonNullable<ReactMyBagAnalyticsInterface["viewCart"]> =
    React.useCallback(
      ({ items, value }) => {
        const signature = items
          .map((item) => `${item.productId}:${item.quantity}`)
          .sort()
          .join("|");
        if (items.length === 0 || sentCartViews.current.has(signature)) {
          return;
        }
        sentCartViews.current.add(signature);
        void identify().then((identity) => {
          if (!identity) {
            sentCartViews.current.delete(signature);
            return;
          }
          return fireCustom("view_cart", {
            category: "engagement",
            subcategory: "page_view",
            cart_key: identity.cartKey,
            items_count: items.length,
            value,
            currency,
          });
        });
      },
      [identify, fireCustom, currency]
    );

  return consumerInitializationGuard<ReactMyBagAnalyticsInterface>({
    initializedInterface: {
      hasInitialized: isConfigurationLoaded,
      checkoutStarted,
      shippingSubmitted,
      contactCaptured,
      removeFromCart,
      viewCart,
    },
  });
};
